import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import utc from 'dayjs/plugin/utc';

dayjs.extend(relativeTime);
dayjs.extend(utc);

/**
 * Display formatters.
 *
 * The backend stores every timestamp as UTC `timestamptz` and every money field
 * as a Prisma `Decimal` serialized to a string. Both are formatted here and
 * nowhere else — a `Number(deal.value)` scattered through components is how
 * rounding errors get into money.
 */

/** ISO order everywhere (`2027-06-30`), matching the platform app. */
export const DATE_FORMAT = 'YYYY-MM-DD';
export const DATE_TIME_FORMAT = 'YYYY-MM-DD, HH:mm';

export function formatDate(value: string | Date | null | undefined): string {
  if (!value) return '—';
  return dayjs(value).format(DATE_FORMAT);
}

export function formatDateTime(value: string | Date | null | undefined): string {
  if (!value) return '—';
  return dayjs(value).format(DATE_TIME_FORMAT);
}

export function formatRelative(value: string | Date | null | undefined): string {
  if (!value) return '—';
  return dayjs(value).fromNow();
}

/**
 * Formats a decimal string from the API without going through `Number` for the
 * value itself where avoidable. Intl handles string input for exact decimals.
 */
export function formatMoney(
  value: string | number | null | undefined,
  currency = 'EUR',
  locale = 'en-GB',
): string {
  if (value === null || value === undefined || value === '') return '—';

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    maximumFractionDigits: 2,
  }).format(typeof value === 'string' ? Number(value) : value);
}

/** Up to two initials for an avatar fallback: "Mossab Kbeisy" → "MK". */
export function initials(name: string | null | undefined): string {
  return (name ?? '')
    .split(/[\s@.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}
